import type { RefObject } from 'react';
import React from 'react';
import classnames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import styles from './index.module.less';
import type { Tools, Theme } from '../../../core/types';
import modifiersToClassNames from '../../../core/modifiersToClassNames';
import { Size, Color } from '../../../core/enums';
import useReturnFocus from '../../../utilities/useReturnFocus';
import withEventTracking from '../../../utilities/withEventTracking';
import { Box } from '../../archived/box';
import { Icon } from '../../atoms/icon';
import { DismissibleContent } from '../../styleless/dismissible-content';
import { FocusTrap } from '../../styleless/focus-trap';
import Element from '../../../core/Element';

interface CloseButtonProps {
  onClick?: () => void;
  tools?: Tools;
  iconTheme: Theme;
  qeId?: string;
  children?: React.ReactNode;
}

const CloseButton = withEventTracking<CloseButtonProps>(
  ({ onClick, qeId, iconTheme }: CloseButtonProps) => (
    <Element
      Component="button"
      type="button"
      qeId={`${qeId}-button-close`}
      className={styles.titleCloseButton}
      onClick={onClick}
      aria-label="Close"
    >
      <Icon
        imageName="dls-icon-close-filled"
        directory="dls"
        theme={iconTheme}
        $size={Size.SM}
      />
    </Element>
  )
);

export const Title = ({
  qeId,
  children,
  toggle,
  $noBorder = false,
  customization = { containerTheme: {}, titleTheme: {}, iconTheme: {} },
  closeButtonTools,
}: {
  qeId?: string;
  children?: React.ReactNode;
  toggle?: () => void;
  $noBorder?: boolean;
  customization?: {
    containerTheme?: Theme;
    iconTheme?: Theme;
    titleTheme?: Theme;
  };
  closeButtonTools?: Tools;
}) => {
  const { containerTheme, titleTheme, iconTheme } = customization;
  const containerThemeWithDefaults = {
    borderColor: Color.Gray04,
    paddingTop: 15,
    paddingRight: 40,
    paddingBottom: 15,
    paddingLeft: 40,
    ...containerTheme,
  };
  const iconThemeWithDefaults = {
    color: Color.BrightBlue,
    ...iconTheme,
  };
  return (
    <Element
      qeId={qeId}
      className={classnames(
        styles.titleComponent,
        modifiersToClassNames(styles, { $titleNoBorder: $noBorder })
      )}
      theme={containerThemeWithDefaults}
    >
      {children && (
        <Element
          Component="h1"
          theme={{
            textHeading: 3,
            color: Color.DeepBlue,
            ...titleTheme,
          }}
          id="dialog1"
        >
          {children}
        </Element>
      )}
      <CloseButton
        qeId={qeId}
        iconTheme={iconThemeWithDefaults}
        onClick={toggle}
        tools={closeButtonTools}
      />
    </Element>
  );
};

Title.displayName = 'ModalTitle';

export const Content = ({
  children,
  theme,
  ignoreDisableBodyScrollFunc,
}: {
  children: React.ReactNode;
  theme?: Theme;
  ignoreDisableBodyScrollFunc?: (el: Element | HTMLElement) => boolean;
}) => {
  const ref = React.createRef<HTMLDivElement>();

  React.useEffect(() => {
    if (ref.current) {
      disableBodyScroll(ref.current, {
        allowTouchMove: ignoreDisableBodyScrollFunc,
      });
    }
    return () => clearAllBodyScrollLocks();
  }, [ignoreDisableBodyScrollFunc, ref]);

  const themeWithDefaults = {
    paddingTop: 30,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    ...theme,
  };

  return (
    <Element
      className={styles.content}
      theme={themeWithDefaults}
      forwardedRef={ref}
    >
      {children}
    </Element>
  );
};

Content.displayName = 'ModalContent';

export interface ContainerProps {
  toggle: () => void;
  children: React.ReactNode;
  $isOpen: boolean;
  $originHeight?: boolean;
  $size?: Size.SM | Size.MD | Size.LG | Size.Auto;
  qeId?: string;
  disableKeyPress?: boolean;
  disableClickOutside?: boolean;
  disableFocus?: boolean;
  ariaLabel?: string;
  focusRef?: RefObject<HTMLElement>;
  dialogRole?: 'dialog' | 'alertdialog' | 'alert';
  ariaLive?: 'off' | 'assertive' | 'polite';
  ariaLabelledby?: string;
  tabIndex?: number;
}

const Container = ({
  toggle,
  children,
  $isOpen,
  $size = Size.LG,
  qeId = 'Modal',
  ariaLabel,
  $originHeight = false,
  disableKeyPress = false,
  disableClickOutside = false,
  disableFocus = false,
  focusRef = undefined,
  dialogRole = 'dialog',
  ariaLive = 'off',
  ariaLabelledby,
  tabIndex = 0,
}: ContainerProps) => {
  if (!$isOpen) {
    clearAllBodyScrollLocks();
  }
  useReturnFocus($isOpen, focusRef);
  const containerSize = $originHeight
    ? '$noScrollContainerSize'
    : '$containerSize';
  return (
    <CSSTransition
      in={$isOpen}
      timeout={300}
      unmountOnExit
      classNames={{
        enter: styles.animationEnter,
        enterActive: styles.animationEnterActive,
        exit: styles.animationExit,
        exitActive: styles.animationExitActive,
      }}
    >
      <div data-qe-id={qeId} className={styles.root}>
        <div className={styles.overlay} />
        <FocusTrap onlyForward={false}>
          <DismissibleContent
            toggle={toggle}
            isOpen={$isOpen}
            disableClickOutside={disableClickOutside}
            disableKeyPress={disableKeyPress}
            disableFocus={disableFocus}
          >
            <div
              className={classnames(
                $originHeight ? styles.noScrollContainer : styles.container,
                modifiersToClassNames(styles, { [containerSize]: $size })
              )}
              role={dialogRole}
              aria-live={ariaLive}
              aria-modal={true}
              aria-label={ariaLabel}
              tabIndex={tabIndex}
              aria-labelledby={ariaLabelledby}
            >
              <Box $glow="light">
                {React.Children.map(children, (child: any) => {
                  const props = child.props || {};
                  const originalType = props.originalType || {};
                  const type = child.type || {};

                  // Injects 'toggle' if it's the Title component, so the Dev doesn't have to pass it manually
                  if (
                    (!props.toggle && type.displayName === 'ModalTitle') ||
                    originalType.displayName === 'ModalTitle'
                  ) {
                    return React.cloneElement(child, { toggle });
                  }

                  return child;
                })}
              </Box>
            </div>
          </DismissibleContent>
        </FocusTrap>
      </div>
    </CSSTransition>
  );
};

export const Modal = { Container, Content, Title };
